import { useEffect, useState } from 'react';
import { api, ErroApi } from './api';
import { Campo, Dialogo, ErroDaApi } from './ui';

/**
 * Os cadastros que aparecem no meio de outro trabalho.
 *
 * Nada aqui é uma tela: são as peças que precisam existir em MAIS DE UM
 * fluxo. A busca com espera de digitação nasceu no recebimento e a de OS
 * usa a mesma; o cadastro rápido de cliente nasceu para desfazer o beco
 * do passo 1 do recebimento e a abertura de OS cai no mesmo beco — a
 * obra exige cliente, e cliente novo aparece exatamente quando se está
 * abrindo a OS da obra nova.
 *
 * Duplicar esses dois seria garantir que um dia eles divergissem: um
 * exigindo CNPJ e o outro não, para o mesmo cadastro.
 */

export interface Cliente { id: string; razao_social: string; cnpj_cpf: string | null }
export interface Funcionario { id: string; nome: string; cargo: string | null }


/**
 * Busca genérica com espera de digitação.
 *
 * Uma consulta por letra é ruído numa rede que já é ruim; 300 ms depois da
 * última tecla é o suficiente para parecer instantâneo e mandar uma só.
 */
export function useBusca<T>(rota: (alvo: string) => string, minimo = 2) {
  const [texto, setTexto] = useState('');
  const [achados, setAchados] = useState<T[] | null>(null);
  const [semPermissao, setSemPermissao] = useState(false);

  useEffect(() => {
    const alvo = texto.trim();
    if (alvo.length < minimo) { setAchados(null); return; }
    const abortar = new AbortController();
    const t = setTimeout(() => {
      api<T[]>(rota(alvo), { sinal: abortar.signal })
        .then((r) => setAchados(r.slice(0, 8)))
        .catch((e) => {
          if ((e as Error).name === 'AbortError') return;
          if (e instanceof ErroApi && e.status === 403) setSemPermissao(true);
          setAchados([]);
        });
    }, 300);
    return () => { clearTimeout(t); abortar.abort(); };
  }, [texto, minimo, rota]);

  return { texto, setTexto, achados, semPermissao };
}

/**
 * Cadastrar o cliente sem sair do recebimento.
 *
 * O campo que pede cliente é obrigatório e só *seleciona* — até 22/08 não
 * havia, em lugar nenhum da interface, como criar um cliente. Quem chegasse
 * ao balcão com amostras de obra nova ficava preso no passo 1 do
 * recebimento: o aviso pedia "informe de quem é o material" e não existia
 * caminho para informar.
 *
 * A saída é aqui, e não numa tela de cadastros à parte, porque o momento em
 * que se descobre que o cliente falta é o momento do trabalho — com as
 * amostras na bancada e o entregador esperando, ou com a obra nova na mão
 * para abrir a OS. Mandar abrir outra tela, cadastrar e voltar significaria
 * refazer o passo inteiro.
 *
 * **Só razão social é exigida.** O CNPJ costuma vir depois, no contrato, e
 * exigi-lo agora empurraria o técnico a digitar qualquer coisa para passar
 * — que é como um cadastro nasce sujo.
 */
export function CadastroRapidoCliente({
  aberto, nomeDigitado, aoFechar, aoCriar,
}: {
  aberto: boolean; nomeDigitado: string;
  aoFechar(): void; aoCriar(c: Cliente): void;
}) {
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [documento, setDocumento] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  // O que ele já digitou na busca é o nome. Redigitar seria pedir duas
  // vezes a mesma informação — e é onde nasce a divergência entre o nome
  // procurado e o nome cadastrado.
  useEffect(() => {
    if (aberto) {
      setRazaoSocial(nomeDigitado);
      setNomeFantasia('');
      setDocumento('');
      setErro(null);
    }
  }, [aberto, nomeDigitado]);

  const soDigitos = documento.replace(/\D/g, '');
  const docValido = soDigitos.length === 0
    || soDigitos.length === 11 || soDigitos.length === 14;

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      const c = await api<Cliente>('/clientes', {
        metodo: 'POST',
        corpo: {
          razaoSocial: razaoSocial.trim(),
          nomeFantasia: nomeFantasia.trim() || undefined,
          cnpjCpf: soDigitos || undefined,
        },
      });
      aoCriar(c);
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  const erroApi = erro instanceof ErroApi ? erro : null;

  return (
    <Dialogo aberto={aberto} titulo="Cadastrar cliente" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Campo
        rotulo="Razão social"
        value={razaoSocial}
        onChange={(e) => setRazaoSocial(e.target.value)}
        erro={erroApi?.campo('razaoSocial')}
        dica="Como aparece no contrato e no relatório. Mínimo de 3 letras."
        autoFocus
      />
      <Campo
        rotulo="Nome fantasia"
        value={nomeFantasia}
        onChange={(e) => setNomeFantasia(e.target.value)}
        erro={erroApi?.campo('nomeFantasia')}
        dica="Opcional. O nome pelo qual o laboratório costuma chamar o cliente."
      />
      <Campo
        rotulo="CNPJ ou CPF"
        inputMode="numeric"
        value={documento}
        onChange={(e) => setDocumento(e.target.value)}
        erro={erroApi?.campo('cnpjCpf')
          ?? (!docValido ? 'CNPJ tem 14 dígitos e CPF tem 11.' : undefined)}
        dica="Opcional agora. Costuma chegar com o contrato, depois do material."
      />
      <div className="linha" style={{ marginTop: '1rem' }}>
        <button
          type="button" className="botao"
          disabled={razaoSocial.trim().length < 3 || !docValido || salvando}
          onClick={() => { void salvar(); }}
        >
          {salvando ? 'Cadastrando…' : 'Cadastrar e usar'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={aoFechar} disabled={salvando}
        >
          Cancelar
        </button>
      </div>
      <p className="dica" style={{ marginTop: '.7rem' }}>
        O cadastro fica registrado em seu nome na trilha de auditoria, com
        data e hora. Ele nunca é excluído — se nascer errado, corrige-se com
        motivo, e a correção também fica registrada.
      </p>
    </Dialogo>
  );
}
